/* PoD Analytics settings: on-time window, aging limits, AWB look-back. Every change is audited on the backend. */
import React, { useEffect, useMemo, useState } from 'react'
import { Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material'
import { LoadingButton } from '@mui/lab'
import { useSnackbar } from 'notistack'
import { get, put } from '../../common/constants'
import { POD_BASE, errText, fmtDate, fmtDateTime } from './podUtils'

const DAYS = [['MONDAY', 'Mon'], ['TUESDAY', 'Tue'], ['WEDNESDAY', 'Wed'], ['THURSDAY', 'Thu'], ['FRIDAY', 'Fri'], ['SATURDAY', 'Sat'], ['SUNDAY', 'Sun']]
  .map(([key, label]) => ({ key, label }))

/** "2, 5, 7" → [2, 5, 7]; null when it is not a list of whole numbers. */
const parseLimits = (text) => {
  const parts = String(text || '').split(',').map((s) => s.trim()).filter(Boolean)
  if (!parts.length || parts.some((p) => !/^\d+$/.test(p))) return null
  return parts.map(Number)
}

/** Same labels the backend builds: [2, 5, 7] → 0–2 days, 3–5 days, 6–7 days, More than 7 days. */
export const previewLabels = (limits) => {
  if (!limits || !limits.length) return []
  const out = []
  let lower = 0
  limits.forEach((l) => {
    out.push(lower === l ? `${l} ${l === 1 ? 'day' : 'days'}` : `${lower}–${l} days`)
    lower = l + 1
  })
  const last = limits[limits.length - 1]
  out.push(`More than ${last} ${last === 1 ? 'day' : 'days'}`)
  return out
}

export default function PodSettingsDialog({ open, onClose, onSaved }) {
  const { enqueueSnackbar } = useSnackbar()
  const [config, setConfig] = useState(null)
  const [onTimeHours, setOnTimeHours] = useState('')
  const [limitsText, setLimitsText] = useState('')
  const [lookback, setLookback] = useState('')
  const [recTarget, setRecTarget] = useState('')
  const [otTarget, setOtTarget] = useState('')
  const [mailLookback, setMailLookback] = useState('')
  const [mailDays, setMailDays] = useState([])
  const [saving, setSaving] = useState(false)
  const [loadError, setLoadError] = useState(null)

  useEffect(() => {
    if (!open) return
    setLoadError(null)
    get(`${POD_BASE}/config`)
      .then(({ data }) => {
        setConfig(data)
        setOnTimeHours(String(data.onTimeHours ?? ''))
        setLimitsText((data.agingBucketDays || []).join(', '))
        setLookback(String(data.awbLookbackDays ?? ''))
        setRecTarget(String(data.receivedTargetPct ?? ''))
        setOtTarget(String(data.onTimeTargetPct ?? ''))
        setMailLookback(String(data.courierMailLookbackDays ?? ''))
        setMailDays(data.courierMailDays || [])
      })
      .catch((e) => setLoadError(errText(e, 'Could not load the PoD settings.')))
  }, [open])

  const limits = useMemo(() => parseLimits(limitsText), [limitsText])
  const limitsOk = limits && limits.every((l, i) => l > 0 && (i === 0 || l > limits[i - 1]))
  const hoursOk = /^\d+$/.test(onTimeHours) && Number(onTimeHours) >= 1 && Number(onTimeHours) <= 720
  const lookbackOk = /^\d+$/.test(lookback) && Number(lookback) >= 15 && Number(lookback) <= 365
  const pctOk = (v) => /^\d+$/.test(v) && Number(v) >= 1 && Number(v) <= 100
  const targetsOk = pctOk(recTarget) && pctOk(otTarget)
  const mailLookbackOk = /^\d+$/.test(mailLookback) && Number(mailLookback) >= 1 && Number(mailLookback) <= 92

  const save = async () => {
    setSaving(true)
    try {
      await put(`${POD_BASE}/config`, { onTimeHours: Number(onTimeHours), agingBucketDays: limits, awbLookbackDays: Number(lookback),
        receivedTargetPct: Number(recTarget), onTimeTargetPct: Number(otTarget),
        courierMailLookbackDays: Number(mailLookback), courierMailDays: mailDays })
      enqueueSnackbar('PoD settings saved', { variant: 'success' })
      onSaved && onSaved()
      onClose()
    } catch (e) {
      enqueueSnackbar(errText(e, 'Could not save the settings.'), { variant: 'error' })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ fontWeight: 700 }}>PoD settings</DialogTitle>
      <DialogContent dividers>
        {loadError && <Alert severity='error' sx={{ mb: 2 }}>{loadError}</Alert>}
        <Stack spacing={3}>
          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>On-time window</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
              A PoD uploaded within this many hours of delivery counts as on time.
            </Typography>
            <TextField size='small' value={onTimeHours} onChange={(e) => setOnTimeHours(e.target.value.replace(/\D/g, ''))}
              error={onTimeHours !== '' && !hoursOk} helperText={onTimeHours !== '' && !hoursOk ? 'Between 1 and 720 hours' : ' '}
              InputProps={{ endAdornment: <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>hours</Typography> }} sx={{ width: 180 }} />
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Aging groups for missing PoDs</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
              Days since delivery where each group ends, separated by commas.
            </Typography>
            <TextField size='small' fullWidth value={limitsText} onChange={(e) => setLimitsText(e.target.value)} placeholder='2, 5, 7'
              error={limitsText !== '' && !limitsOk} helperText={limitsText !== '' && !limitsOk ? 'Whole days above 0, each larger than the one before, e.g. 2, 5, 7' : ' '} />
            {limitsOk && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {previewLabels(limits).map((l) => <Chip key={l} size='small' label={l} variant='outlined' sx={{ '& .MuiChip-label': { textTransform: 'none' } }} />)}
              </Box>
            )}
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>AWB look-back</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
              How many days before the selected delivery dates a shipment's AWB can have been created. Keep it longer than your slowest
              pickup-to-delivery time, or those shipments are missed.
            </Typography>
            <TextField size='small' value={lookback} onChange={(e) => setLookback(e.target.value.replace(/\D/g, ''))}
              error={lookback !== '' && !lookbackOk} helperText={lookback !== '' && !lookbackOk ? 'Between 15 and 365 days' : ' '}
              InputProps={{ endAdornment: <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>days</Typography> }} sx={{ width: 180 }} />
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Targets</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
              Couriers and clients below these are flagged in "Needs attention" and the scorecard.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TextField size='small' label='PoDs received' value={recTarget} onChange={(e) => setRecTarget(e.target.value.replace(/\D/g, ''))}
                error={recTarget !== '' && !pctOk(recTarget)} helperText={recTarget !== '' && !pctOk(recTarget) ? 'Between 1 and 100%' : ' '}
                InputProps={{ endAdornment: <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>%</Typography> }} sx={{ width: 170 }} />
              <TextField size='small' label='On time' value={otTarget} onChange={(e) => setOtTarget(e.target.value.replace(/\D/g, ''))}
                error={otTarget !== '' && !pctOk(otTarget)} helperText={otTarget !== '' && !pctOk(otTarget) ? 'Between 1 and 100%' : ' '}
                InputProps={{ endAdornment: <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>%</Typography> }} sx={{ width: 170 }} />
            </Box>
          </Box>

          <Box>
            <Typography sx={{ fontWeight: 600, mb: 0.5 }}>Courier reminder email</Typography>
            <Typography sx={{ fontSize: 13, color: 'text.secondary', mb: 1 }}>
              Chase shipments delivered in the last N days, and send the 10:00 email on these days. Switch it on or off on the page.
            </Typography>
            <TextField size='small' label='Look back' value={mailLookback} onChange={(e) => setMailLookback(e.target.value.replace(/\D/g, ''))}
              error={mailLookback !== '' && !mailLookbackOk} helperText={mailLookback !== '' && !mailLookbackOk ? 'Between 1 and 92 days' : ' '}
              InputProps={{ endAdornment: <Typography sx={{ fontSize: 13, color: 'text.secondary' }}>days</Typography> }} sx={{ width: 170 }} />
            <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }} role='group' aria-label='Days the email is sent'>
              {DAYS.map((d) => {
                const on = mailDays.includes(d.key)
                return <Chip key={d.key} label={d.label} clickable color={on ? 'primary' : 'default'} variant={on ? 'filled' : 'outlined'} aria-pressed={on}
                  onClick={() => setMailDays((v) => (on ? v.filter((x) => x !== d.key) : [...v, d.key]))} sx={{ '& .MuiChip-label': { textTransform: 'none' } }} />
              })}
            </Box>
            {mailDays.length === 0 && <Typography sx={{ fontSize: 12, color: 'warning.dark', mt: 0.5 }}>No day picked: the 10:00 email will not go out.</Typography>}
          </Box>

          {config && (
            <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
              Upload times are recorded from {config.trackingStartDate ? fmtDate(config.trackingStartDate) : 'go-live'}.
              {config.updatedBy && ` Last changed by ${config.updatedBy}${config.updatedAt ? ` on ${fmtDateTime(String(config.updatedAt).replace(' ', 'T'))}` : ''}.`}
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 1.5 }}>
        <Button onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <LoadingButton variant='contained' loading={saving} disabled={!config || !hoursOk || !limitsOk || !lookbackOk || !targetsOk || !mailLookbackOk} onClick={save} sx={{ textTransform: 'none' }}>
          Save settings
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
